/**
 * client/render/players.ts — third-person player bodies, anims, held weapons. OWNER: RENDER agent.
 *
 * Bodies: models/body_seals_assault.glb (PlayerInfo.body 0, allies = SEALs) and body_pmc_assault.glb
 * (body 1, axis = PMC) — SnS uses the assault body for the crossbow (research/weapons-mp.md §6,
 * models-anims.md §2). Anims: anims/player_{move,events,death}.glb, chosen client-side by animrules.ts
 * from RenderPlayer state + `ent`/`game` frame events (snapshots carry legsAnim/torsoAnim = 0).
 *
 * Layering (engine anim tree, ESTIMATE on details): a state anim ("both") drives legs + torso; torso events
 * (fire/reload/raise/melee/throw) override the torso bones (j_spinelower subtree) with a fade; legs events
 * (jump/land) override the rest; "both" events (stance transitions, dive land) override everything; death
 * plays on everything and holds the last frame (no ragdoll), with its tag_origin root motion (delta).
 * Move anims are rate-scaled by speed / (anim root-motion speed) (engine scales move anims by velocity).
 * Aim pitch is procedural (BG_Player_DoControllers, models-anims.md §2; T5 weights, ESTIMATE for T6).
 *
 * Lighting: ambient irradiance from the nearest static-model probes (world.ts LightProbe; a stand-in for the
 * engine's light grid, ESTIMATE) + sun with shadows + the dynamic light.
 */
import * as THREE from 'three';
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as skeletonClone } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { getWeaponDef } from '@shared/weapons/defs';
import { GameEventType, MoveFlag, PEv, WeaponId, type WeaponId as WeaponIdT } from '@shared/types';
import type { ClientWorld, FrameEvent, RenderPlayer } from '../types';
import {
  animContext, dmgDirection, dmgTypeForMod, eventBlock, matchRule, pickAnim, ruleExtras, stanceBlock, stateBlock,
  type AnimContext, type AnimRulesFile, type RuleAnim,
} from './animrules';
import { placeCodYaw } from './codxform';
import type { Bo2Uniforms } from './glsl';
import { layersOf, mergeLayered } from './merge';
import { makeBo2ModelMaterial, makeShadowDepthMaterial } from './modelmat';
import { disposeSkeletons, presetSkinnedBounds, type ModelCache } from './modelcache';
import type { LightProbe } from './world';

interface ClipInfo {
  duration: number;
  looped: boolean;
  numFrames: number;
  framerate: number;
  delta?: { trans?: { frames: number[]; values: number[][] } };
}

const BODY_FILES = ['models/body_seals_assault.glb', 'models/body_pmc_assault.glb'];
const ANIM_FILES = ['anims/player_move.glb', 'anims/player_events.glb', 'anims/player_death.glb'];
const WEAPON_FILES: Partial<Record<WeaponIdT, string>> = {
  [WeaponId.CROSSBOW]: 'models/w_crossbow_world.glb',
  [WeaponId.KNIFE_BALLISTIC]: 'models/w_ballistic_knife_world.glb',
  [WeaponId.HATCHET]: 'models/w_hatchet_world.glb',
  [WeaponId.KNIFE]: 'models/w_knife_melee.glb',
};

const NO_ANIM = typeof location !== 'undefined' && new URLSearchParams(location.search).get('noanim') === '1';
/** Default blend times (ms). ESTIMATE where the script gives none. */
const STATE_BLEND_MS = 200;
const EVENT_BLEND_IN_MS = 100;
const EVENT_BLEND_OUT_MS = 150;
/** Aim controllers: bone -> pitch weight (T5 player_AimBlend_*; pelvis' 0.4 moved to back_low so the feet stay
 *  planted, and the sum normalised to 1 so the head follows the view pitch — ESTIMATE). */
const AIM_BONES: [string, number][] = [['back_low', 0.4 / 1.3], ['back_mid', 0.1 / 1.3], ['j_spine4', 0.5 / 1.3], ['neck', 0.3 / 1.3]];

interface PartAction { action: THREE.AnimationAction; }
interface BaseEntry { name: string; legs: THREE.AnimationAction; torso: THREE.AnimationAction; w: number; target: number; fade: number; moveSpeed: number }
interface EventEntry { name: string; actions: THREE.AnimationAction[]; part: 'legs' | 'torso' | 'both'; w: number; fadeIn: number; fadeOut: number; dur: number; hold: boolean; ending: boolean }

/** Shared, loaded once. */
export class PlayerAssets {
  bodies: GLTF[] = [];
  clips = new Map<string, THREE.AnimationClip>();
  info: Record<string, ClipInfo> = {};
  rules: AnimRulesFile = { blocks: {} };
  weapons = new Map<WeaponIdT, GLTF>();
  torsoBones = new Set<string>();
  bodyBones = new Set<string>();
  private filtered = new Map<string, { legs: THREE.AnimationClip; torso: THREE.AnimationClip; both: THREE.AnimationClip }>();
  moveSpeed = new Map<string, number>();
  mainrootBind: THREE.Vector3 | null = null;

  constructor(private readonly cache: ModelCache, private readonly renderer: THREE.WebGLRenderer) {}

  async load(onProgress?: (f: number, l: string) => void): Promise<void> {
    let done = 0;
    const total = BODY_FILES.length + ANIM_FILES.length + Object.keys(WEAPON_FILES).length + 2;
    const tick = (l: string) => onProgress?.(++done / total, l);
    const [bodies, anims, weapons, info, rules] = await Promise.all([
      Promise.all(BODY_FILES.map((f) => this.cache.load('/assets/' + f).then((g) => { tick('players'); return g; }))),
      Promise.all(ANIM_FILES.map((f) => this.cache.load('/assets/' + f).then((g) => { tick('player anims'); return g; }))),
      Promise.all(Object.entries(WEAPON_FILES).map(([id, f]) => this.cache.load('/assets/' + f).then((g) => { tick('weapons'); return [Number(id) as WeaponIdT, g] as const; }))),
      fetch('/assets/anims/player_anims.json').then((r) => r.json()).then((j) => { tick('anim table'); return j as { clips: Record<string, ClipInfo> }; }),
      fetch('/assets/anims/playeranim_rules_sns.json').then((r) => r.json()).then((j) => { tick('anim rules'); return j as AnimRulesFile; }),
    ]);
    // one draw per body / weapon: merge the material groups into texture-array meshes
    for (const b of bodies) await mergeLayered(b.scene, this.renderer);
    for (const [, g] of weapons) await mergeLayered(g.scene, this.renderer);
    this.bodies = bodies;
    for (const g of anims) for (const c of g.animations) this.clips.set(c.name, c);
    for (const [id, g] of weapons) this.weapons.set(id, g);
    this.info = info.clips;
    this.rules = rules;
    // bone sets from the SEAL skeleton (all four bodies share bone names, models-anims.md §2)
    bodies[0].scene.traverse((o) => { if ((o as THREE.Bone).isBone || o.name) this.bodyBones.add(o.name); });
    const mr = bodies[0].scene.getObjectByName('j_mainroot');
    if (mr) this.mainrootBind = mr.position.clone();
    const spine = bodies[0].scene.getObjectByName('j_spinelower');
    spine?.traverse((o) => this.torsoBones.add(o.name));
    for (const [name, ci] of Object.entries(this.info)) {
      const tr = ci.delta?.trans;
      if (tr && tr.values.length >= 2 && ci.duration > 0) {
        const a = tr.values[0], b = tr.values[tr.values.length - 1];
        this.moveSpeed.set(name, Math.hypot(b[0] - a[0], b[1] - a[1]) / ci.duration);
      }
    }
  }

  /** Clip split into legs / torso / both track sets (only bones the bodies have). */
  parts(name: string): { legs: THREE.AnimationClip; torso: THREE.AnimationClip; both: THREE.AnimationClip } | null {
    const hit = this.filtered.get(name);
    if (hit) return hit;
    const c = this.clips.get(name);
    if (!c) return null;
    const legs: THREE.KeyframeTrack[] = [], torso: THREE.KeyframeTrack[] = [], both: THREE.KeyframeTrack[] = [];
    for (let t of c.tracks) {
      const bone = t.name.slice(0, t.name.lastIndexOf('.'));
      if (!this.bodyBones.has(bone)) continue;
      if (bone === 'j_mainroot' && t.name.endsWith('.position') && this.mainrootBind) {
        // The player xanims store the ROOT bone translation relative to its bind position (unlike every other
        // bone, which is absolute): pb_stand_alert z = -4.4, pb_crouch_alert -19.2, pb_prone_aim -32.2 against
        // a bind height of 37.2 -> pelvis at 33 / 18 / 5 in. Add the bind back (verified by those heights).
        const v = Float32Array.from(t.values as Float32Array);
        const b = this.mainrootBind;
        for (let i = 0; i < v.length; i += 3) { v[i] += b.x; v[i + 1] += b.y; v[i + 2] += b.z; }
        t = new THREE.VectorKeyframeTrack(t.name, t.times as unknown as number[], v as unknown as number[]);
      }
      both.push(t);
      (this.torsoBones.has(bone) ? torso : legs).push(t);
    }
    const r = {
      legs: new THREE.AnimationClip(name + '#legs', c.duration, legs),
      torso: new THREE.AnimationClip(name + '#torso', c.duration, torso),
      both: new THREE.AnimationClip(name + '#both', c.duration, both),
    };
    this.filtered.set(name, r);
    return r;
  }
}

/** Light-probe lookup (inverse-distance weighted nearest static-model irradiance). */
export class ProbeGrid {
  private readonly cells = new Map<number, LightProbe[]>();
  private static readonly CELL = 192;
  readonly fallback = new THREE.Vector3(0.9, 0.9, 0.9); // median world lightmap irradiance [MAP]
  constructor(probes: LightProbe[]) {
    for (const p of probes) {
      const k = this.key(Math.floor(p.pos.x / ProbeGrid.CELL), Math.floor(p.pos.y / ProbeGrid.CELL), Math.floor(p.pos.z / ProbeGrid.CELL));
      let c = this.cells.get(k);
      if (!c) { c = []; this.cells.set(k, c); }
      c.push(p);
    }
  }
  private key(x: number, y: number, z: number): number { return ((x + 512) * 1024 + (y + 512)) * 1024 + (z + 512); }
  sample(out: THREE.Vector3, pos: THREE.Vector3): THREE.Vector3 {
    const C = ProbeGrid.CELL;
    const cx = Math.floor(pos.x / C), cy = Math.floor(pos.y / C), cz = Math.floor(pos.z / C);
    let wsum = 0;
    out.set(0, 0, 0);
    for (let dx = -1; dx <= 1; dx++) for (let dy = -1; dy <= 1; dy++) for (let dz = -1; dz <= 1; dz++) {
      const c = this.cells.get(this.key(cx + dx, cy + dy, cz + dz));
      if (!c) continue;
      for (const p of c) {
        const d2 = p.pos.distanceToSquared(pos);
        if (d2 > C * C * 2.25) continue;
        const w = 1 / (d2 + 400);
        out.addScaledVector(p.e, w);
        wsum += w;
      }
    }
    if (wsum <= 0) return out.copy(this.fallback);
    return out.multiplyScalar(1 / wsum);
  }
}

function hash01(a: number, b: number, c: number): number {
  let h = Math.imul(a ^ 0x9e3779b9, 0x85ebca6b) ^ Math.imul(b + 0x632be59b, 0xc2b2ae35) ^ Math.imul(c + 1, 0x27d4eb2f);
  h ^= h >>> 15; h = Math.imul(h, 0x2c1b3c6d); h ^= h >>> 12;
  return ((h >>> 0) % 100000) / 100000;
}

export class PlayerView {
  readonly root = new THREE.Group();
  private readonly body: THREE.Object3D;
  private readonly mixer: THREE.AnimationMixer;
  private readonly materials: THREE.ShaderMaterial[] = [];
  readonly shadowProxies: THREE.SkinnedMesh[] = [];
  private readonly aimBones: [THREE.Object3D, number][] = [];
  private readonly aimRest: THREE.Quaternion[] = [];
  private readonly tagRight: THREE.Object3D | null;
  private readonly tagLeft: THREE.Object3D | null;
  private readonly weapons = new Map<WeaponIdT, THREE.Object3D>();
  private readonly weaponMats: THREE.ShaderMaterial[] = [];
  private base: BaseEntry[] = [];
  private events: EventEntry[] = [];
  private dead = false;
  private deathName = '';
  private deathAction: THREE.AnimationAction | null = null;
  private deathDelta: ClipInfo['delta'] | null = null;
  private readonly deathOffset = new THREE.Vector3();
  private lastStance = -1;
  private lastSpawnCount = -1;
  private lastAlive = true;
  private offhandUntil = 0;
  private knifeUntil = 0;
  private stateKey = '';
  private readonly ctx: AnimContext = {};
  private readonly ambient = new THREE.Vector3(1, 1, 1);
  private readonly tmpV = new THREE.Vector3();
  private readonly tmpQ = new THREE.Quaternion();
  private readonly tmpQ2 = new THREE.Quaternion();
  private readonly axis = new THREE.Vector3();
  private time = 0;
  visible = false;
  readonly flashPos = new THREE.Vector3();

  constructor(readonly clientNum: number, readonly bodyIndex: number, private readonly A: PlayerAssets, private readonly U: Bo2Uniforms, shadowMat: THREE.Material) {
    const src = A.bodies[bodyIndex % A.bodies.length];
    this.body = skeletonClone(src.scene);
    this.root.add(this.body);
    this.root.name = `player_${clientNum}`;
    this.body.traverse((o) => {
      const m = o as THREE.SkinnedMesh;
      if (!m.isMesh) return;
      m.frustumCulled = false;
      const mats = Array.isArray(m.material) ? m.material : [m.material];
      const layers = layersOf(m.geometry);
      const nm = mats.map((x) => { const bm = makeBo2ModelMaterial(x, U, layers ? { layers } : {}); this.materials.push(bm); return bm; });
      m.material = Array.isArray(m.material) ? nm : nm[0];
      if (m.isSkinnedMesh) {
        // one-draw shadow proxy sharing the skeleton (geometry groups dropped)
        const g = new THREE.BufferGeometry();
        for (const [k, a] of Object.entries(m.geometry.attributes)) g.setAttribute(k, a);
        g.setIndex(m.geometry.index);
        const proxy = new THREE.SkinnedMesh(g, shadowMat);
        proxy.bind(m.skeleton, m.bindMatrix);
        proxy.frustumCulled = false;
        presetSkinnedBounds(proxy);
        this.shadowProxies.push(proxy);
      }
    });
    presetSkinnedBounds(this.body);
    this.mixer = new THREE.AnimationMixer(this.body);
    for (const [name, w] of AIM_BONES) {
      const b = this.body.getObjectByName(name);
      if (b) { this.aimBones.push([b, w]); this.aimRest.push(b.quaternion.clone()); }
    }
    this.tagRight = this.body.getObjectByName('tag_weapon_right') ?? null;
    this.tagLeft = this.body.getObjectByName('tag_weapon_left') ?? null;
    for (const [id, g] of A.weapons) {
      const w = skeletonClone(g.scene);
      const codRoot = w.getObjectByName('cod_root');
      // the weapon's CoD-local frame goes straight onto the CoD-local tag (cod_root conversion not needed)
      if (codRoot) codRoot.quaternion.identity();
      w.traverse((o) => {
        if (o !== w) o.name = 'wpn_' + o.name; // keep anim bindings on the body unambiguous
        const m = o as THREE.Mesh;
        if (!m.isMesh) return;
        m.frustumCulled = false;
        const mats = Array.isArray(m.material) ? m.material : [m.material];
        // '*_camo1..3' materials are separate parts (the camo-able pieces), all drawn (merged: one draw)
        const layers = layersOf(m.geometry);
        const nm = mats.map((x) => { const bm = makeBo2ModelMaterial(x, U, layers ? { layers } : {}); this.weaponMats.push(bm); return bm; });
        m.material = Array.isArray(m.material) ? nm : nm[0];
      });
      w.visible = false;
      presetSkinnedBounds(w);
      const tag = id === WeaponId.KNIFE ? this.tagLeft : this.tagRight;
      (tag ?? this.body).add(w);
      this.weapons.set(id, w);
    }
  }

  /** Warm-up only (renderer load): make every held weapon visible so its program/textures get compiled. */
  showAllWeapons(on: boolean): void {
    for (const w of this.weapons.values()) w.visible = on;
  }

  /** Per-frame update. Returns whether the body is drawn. */
  update(p: RenderPlayer, dt: number, probes: ProbeGrid, hidden: boolean): boolean {
    this.time += dt;
    // respawn: reset everything (no interpolation/anim carry-over across spawnCount)
    if (p.spawnCount !== this.lastSpawnCount) {
      this.lastSpawnCount = p.spawnCount;
      this.resetAnims();
      this.lastStance = p.stance;
    }
    if (!p.alive && this.lastAlive && !this.dead) this.startDeath(p, 0, 0); // death without a DEATH event (fallback)
    this.lastAlive = p.alive;
    if (p.alive && this.dead) this.resetAnims();
    placeCodYaw(this.root, p.origin, p.angles[1]);
    this.visible = !hidden && (p.alive || this.dead);
    this.root.visible = this.visible;
    if (!this.visible) return false;
    if (!this.dead) {
      if (p.stance !== this.lastStance) {
        if (this.lastStance >= 0) {
          const blk = stanceBlock(this.lastStance, p.stance);
          if (blk) this.playEvent(blk, p, p.weapon);
        }
        this.lastStance = p.stance;
      }
      this.updateState(p);
    }
    this.updateWeights(dt);
    // controller bones are not (all) animated: restore their rest pose so the aim offset never accumulates
    for (let i = 0; i < this.aimBones.length; i++) this.aimBones[i][0].quaternion.copy(this.aimRest[i]);
    if (!NO_ANIM) this.mixer.update(dt);
    if (this.dead) this.updateDeathDelta(p);
    // procedural aim pitch
    this.body.updateMatrixWorld(true);
    if (!this.dead) this.applyAim(p);
    // weapon visibility
    const now = this.time;
    const showOffhand = now < this.offhandUntil;
    const showKnife = now < this.knifeUntil;
    for (const [id, w] of this.weapons) {
      let vis = false;
      if (id === WeaponId.KNIFE) vis = showKnife;
      else if (showOffhand) vis = id === WeaponId.HATCHET;
      else vis = id === p.weapon;
      w.visible = vis && !this.dead;
    }
    // lighting
    this.tmpV.copy(this.root.position); this.tmpV.y += 40;
    probes.sample(this.ambient, this.tmpV);
    for (const m of this.materials) (m.uniforms.uAmbient.value as THREE.Vector3).copy(this.ambient);
    for (const m of this.weaponMats) (m.uniforms.uAmbient.value as THREE.Vector3).copy(this.ambient);
    return true;
  }

  /** Muzzle position (three space) of the held weapon, for flashes. */
  muzzle(out: THREE.Vector3): THREE.Vector3 {
    for (const [, w] of this.weapons) {
      if (!w.visible) continue;
      const f = w.getObjectByName('wpn_tag_flash');
      if (f) return f.getWorldPosition(out);
    }
    return this.tagRight ? this.tagRight.getWorldPosition(out) : out.copy(this.root.position).setY(this.root.position.y + 50);
  }

  onEvent(ev: number, parm: number, p: RenderPlayer): void {
    if (this.dead) return;
    const e = eventBlock(ev, parm, p.weapon);
    if (ev === PEv.OFFHAND_START) this.offhandUntil = this.time + 1.0;
    if (ev === PEv.OFFHAND_THROW) this.offhandUntil = this.time + 0.35;
    if (ev === PEv.MELEE_SWING || ev === PEv.MELEE_LUNGE) {
      // crossbow melee uses knife_mp (useAsMelee 0): the knife appears in the left hand (ESTIMATE)
      if (p.weapon !== WeaponId.KNIFE_BALLISTIC) this.knifeUntil = this.time + (ev === PEv.MELEE_LUNGE ? 1.16 : 0.8);
    }
    if (!e) return;
    this.playEvent(e.block, p, e.weapon, ev);
  }

  onDeath(p: RenderPlayer, mod: number, dirYaw: number): void {
    if (this.dead) return;
    this.startDeath(p, mod, dirYaw);
  }

  // ---------------------------------------------------------------------------------------------
  private context(p: RenderPlayer, weapon: WeaponIdT): AnimContext {
    return animContext({ weapon, stance: p.stance, moveFlags: p.moveFlags, velocity: p.velocity, yaw: p.angles[1] }, this.ctx);
  }

  private updateState(p: RenderPlayer): void {
    const block = stateBlock({ weapon: p.weapon, stance: p.stance, moveFlags: p.moveFlags, velocity: p.velocity, yaw: p.angles[1] });
    const ctx = this.context(p, p.weapon);
    const key = `${block}|${ctx.playeranimtype}|${ctx.stance}|${ctx.direction}|${ctx.weapon_position}`;
    const cur = this.base.find((b) => b.target === 1);
    if (key !== this.stateKey || !cur) {
      this.stateKey = key;
      const rule = matchRule(this.A.rules, block, ctx);
      const a = pickAnim(rule, hash01(this.clientNum, p.spawnCount, key.length));
      if (a && (!cur || cur.name !== a.anim)) this.setBase(a, cur);
    }
    // rate-scale move anims to the actual speed
    const live = this.base.find((b) => b.target === 1);
    if (live && live.moveSpeed > 1) {
      const speed = Math.hypot(p.velocity[0], p.velocity[1]);
      const ts = THREE.MathUtils.clamp(speed / live.moveSpeed, 0.5, 1.8);
      live.legs.timeScale = live.torso.timeScale = ts;
    }
    // jump: hold the takeoff pose while airborne
    for (const e of this.events) if (e.hold && (p.moveFlags & MoveFlag.ON_GROUND)) e.hold = false;
  }

  private setBase(a: RuleAnim, prev: BaseEntry | undefined): void {
    const parts = this.A.parts(a.anim);
    if (!parts) return;
    const ex = ruleExtras(a);
    const blend = (typeof ex.blendtime === 'number' ? ex.blendtime : STATE_BLEND_MS) / 1000;
    const legs = this.mixer.clipAction(parts.legs);
    const torso = this.mixer.clipAction(parts.torso);
    const info = this.A.info[a.anim];
    const loop = info?.looped !== false;
    for (const act of [legs, torso]) {
      act.reset();
      act.setLoop(loop ? THREE.LoopRepeat : THREE.LoopOnce, Infinity);
      act.clampWhenFinished = !loop;
      act.setEffectiveWeight(0);
      act.play();
    }
    // locomotion phase sync (left/right/forward cycles share foot timing)
    if (prev && loop && prev.legs.getClip().duration > 0) {
      const ph = (prev.legs.time / prev.legs.getClip().duration) % 1;
      legs.time = torso.time = ph * parts.legs.duration;
    }
    for (const b of this.base) { b.target = 0; b.fade = blend; }
    const existing = this.base.find((b) => b.name === a.anim);
    if (existing) { existing.target = 1; existing.fade = blend; return; }
    this.base.push({ name: a.anim, legs, torso, w: this.base.length ? 0 : 1, target: 1, fade: blend, moveSpeed: this.A.moveSpeed.get(a.anim) ?? 0 });
  }

  private playEvent(block: string, p: RenderPlayer, weapon: WeaponIdT, ev = 0): void {
    const ctx = this.context(p, weapon);
    const rule = matchRule(this.A.rules, block, ctx);
    const a = pickAnim(rule, hash01(this.clientNum, Math.floor(this.time * 1000), ev));
    if (!a) return;
    const parts = this.A.parts(a.anim);
    if (!parts) return;
    const ex = ruleExtras(a);
    const part = a.part;
    // replace an event already playing on the same part(s)
    for (const e of this.events) if (e.part === part || part === 'both' || e.part === 'both') e.ending = true;
    const clips = part === 'both' ? [parts.legs, parts.torso] : [part === 'legs' ? parts.legs : parts.torso];
    const actions = clips.map((c) => {
      const act = this.mixer.clipAction(c);
      act.reset();
      act.setLoop(THREE.LoopOnce, 1);
      act.clampWhenFinished = true;
      act.setEffectiveWeight(0);
      act.play();
      return act;
    });
    let dur = parts.legs.duration;
    // 'duration' (ms) = played length; tiny values (jump 'duration 5') are not durations -> ignore (ESTIMATE)
    if (typeof ex.duration === 'number' && ex.duration >= 50) {
      const ts = dur / (ex.duration / 1000);
      for (const act of actions) act.timeScale = ts;
      dur = ex.duration / 1000;
    } else if (ex.weapontimescale && (block === 'EVENTS/raiseweapon' || block === 'EVENTS/firstraiseweapon')) {
      // 'weaponTimeScale': fit the anim to the weapon's raise time [WPN raiseTime / firstRaiseTime]
      const d = getWeaponDef(weapon);
      const t = (block === 'EVENTS/firstraiseweapon' ? d.firstRaiseTimeMs : d.raiseTimeMs) / 1000;
      if (t > 0.05) { for (const act of actions) act.timeScale = dur / t; dur = t; }
    }
    const blendIn = (typeof ex.blendtime === 'number' ? Math.max(ex.blendtime, 30) : EVENT_BLEND_IN_MS) / 1000;
    const blendOut = (typeof ex.blendouttime === 'number' ? Math.max(ex.blendouttime, 30) : EVENT_BLEND_OUT_MS) / 1000;
    const hold = block === 'EVENTS/jump' && !(p.moveFlags & MoveFlag.ON_GROUND);
    this.events.push({ name: a.anim, actions, part, w: 0, fadeIn: blendIn, fadeOut: blendOut, dur, hold, ending: false });
  }

  private updateWeights(dt: number): void {
    // events
    let evL = 0, evT = 0;
    for (const e of this.events) {
      const t = e.actions[0].time / Math.max(1e-3, e.actions[0].getClip().duration) * e.dur;
      if (!e.ending && !e.hold && t >= e.dur - e.fadeOut) e.ending = true;
      if (e.ending) e.w = Math.max(0, e.w - dt / e.fadeOut);
      else e.w = Math.min(1, e.w + dt / e.fadeIn);
      for (const a of e.actions) a.setEffectiveWeight(e.w);
      if (e.part !== 'torso') evL = Math.max(evL, e.w);
      if (e.part !== 'legs') evT = Math.max(evT, e.w);
    }
    this.events = this.events.filter((e) => {
      if (e.ending && e.w <= 0) { for (const a of e.actions) a.stop(); return false; }
      return true;
    });
    // base crossfade
    for (const b of this.base) {
      const step = b.fade > 0 ? dt / b.fade : 1;
      b.w = b.target > b.w ? Math.min(b.target, b.w + step) : Math.max(b.target, b.w - step);
      b.legs.setEffectiveWeight(b.w * (1 - evL));
      b.torso.setEffectiveWeight(b.w * (1 - evT));
    }
    this.base = this.base.filter((b) => {
      if (b.target === 0 && b.w <= 0) { b.legs.stop(); b.torso.stop(); return false; }
      return true;
    });
  }

  private applyAim(p: RenderPlayer): void {
    // CoD pitch: + = looking down. Rotate about the body's right axis (three) by -pitch * weight.
    let pitch = p.angles[0];
    if (pitch > 180) pitch -= 360;
    pitch = THREE.MathUtils.clamp(pitch, p.stance === 2 ? -30 : -70, p.stance === 2 ? 30 : 70);
    if (Math.abs(pitch) < 0.01) return;
    this.axis.set(0, 0, 1).applyQuaternion(this.root.quaternion); // root-local +Z = right (three)
    for (const [bone, w] of this.aimBones) {
      const ang = (-pitch * w * Math.PI) / 180;
      // world-space delta -> bone-local: q_local = inv(parentWorld) * q_world * parentWorld * q_bone
      bone.parent!.getWorldQuaternion(this.tmpQ);
      this.tmpQ2.setFromAxisAngle(this.axis, ang);
      const inv = this.tmpQ.clone().invert();
      bone.quaternion.premultiply(this.tmpQ).premultiply(this.tmpQ2).premultiply(inv);
      bone.updateMatrixWorld(true);
    }
  }

  private startDeath(p: RenderPlayer, mod: number, dirYaw: number): void {
    const ctx = this.context(p, p.weapon);
    ctx.dmgtype = dmgTypeForMod(mod);
    ctx.dmgdirection = dmgDirection(dirYaw, p.angles[1]);
    const rule = matchRule(this.A.rules, 'DEATH', ctx);
    const a = pickAnim(rule, hash01(this.clientNum, p.spawnCount, 77));
    this.resetAnims();
    this.dead = true;
    if (!a) return;
    const parts = this.A.parts(a.anim);
    if (!parts) return;
    const act = this.mixer.clipAction(parts.both);
    act.reset();
    act.setLoop(THREE.LoopOnce, 1);
    act.clampWhenFinished = true;
    act.setEffectiveWeight(1);
    act.play();
    this.deathAction = act;
    this.deathName = a.anim;
    this.deathDelta = this.A.info[a.anim]?.delta ?? null;
    this.deathOffset.set(0, 0, 0);
  }

  /** Death root motion: delta.trans (CoD, body-local x fwd / y left) sampled at the action time. */
  private updateDeathDelta(p: RenderPlayer): void {
    const tr = this.deathDelta?.trans;
    const act = this.deathAction;
    const info = this.A.info[this.deathName];
    if (!tr || !act || !info) return;
    const f = act.time * info.framerate;
    const fr = tr.frames;
    let i = 0;
    while (i < fr.length - 2 && fr[i + 1] < f) i++;
    const t = fr[i + 1] > fr[i] ? THREE.MathUtils.clamp((f - fr[i]) / (fr[i + 1] - fr[i]), 0, 1) : 0;
    const a = tr.values[i], b = tr.values[Math.min(i + 1, tr.values.length - 1)];
    const lx = a[0] + (b[0] - a[0]) * t, ly = a[1] + (b[1] - a[1]) * t;
    const yaw = (p.angles[1] * Math.PI) / 180;
    const cx = lx * Math.cos(yaw) - ly * Math.sin(yaw), cy = lx * Math.sin(yaw) + ly * Math.cos(yaw);
    this.deathOffset.set(cx, 0, -cy);
    this.root.position.add(this.deathOffset);
  }

  private resetAnims(): void {
    this.mixer.stopAllAction();
    this.base = [];
    this.events = [];
    this.dead = false;
    this.deathAction = null;
    this.deathDelta = null;
    this.deathOffset.set(0, 0, 0);
    this.deathName = '';
    this.stateKey = '';
  }

  debug(): Record<string, unknown> {
    return {
      dead: this.dead, death: this.deathName,
      base: this.base.map((b) => ({ n: b.name, w: +b.w.toFixed(2), t: +b.legs.time.toFixed(2), ts: +b.legs.timeScale.toFixed(2), lw: +b.legs.getEffectiveWeight().toFixed(2), run: b.legs.isRunning() })),
      events: this.events.map((e) => ({ n: e.name, w: +e.w.toFixed(2), part: e.part })),
    };
  }

  dispose(): void {
    this.mixer.stopAllAction();
    for (const m of [...this.materials, ...this.weaponMats]) m.dispose();
    for (const p of this.shadowProxies) p.geometry.dispose();
    disposeSkeletons(this.root); // body + weapon clone skeletons (bone textures); proxies share the body's
  }
}

/** All player views. */
export class PlayerRenderer {
  readonly assets: PlayerAssets;
  private readonly views = new Map<number, PlayerView>();
  private probes!: ProbeGrid;
  private readonly shadowMat = makeShadowDepthMaterial();
  private readonly frustum = new THREE.Frustum();
  private readonly pv = new THREE.Matrix4();
  private readonly sphere = new THREE.Sphere();
  visibleCount = 0;
  /** Muzzle flashes requested this frame (three-space positions). */
  readonly flashes: THREE.Vector3[] = [];

  constructor(cache: ModelCache, renderer: THREE.WebGLRenderer, private readonly U: Bo2Uniforms, private readonly scene: THREE.Scene, private readonly shadowScene: THREE.Scene) {
    this.assets = new PlayerAssets(cache, renderer);
  }

  async load(probes: LightProbe[], onProgress?: (f: number, l: string) => void): Promise<void> {
    this.probes = new ProbeGrid(probes);
    await this.assets.load(onProgress);
  }

  get(clientNum: number): PlayerView | undefined { return this.views.get(clientNum); }
  debug(): Record<string, unknown> {
    const o: Record<string, unknown> = {};
    for (const [cn, v] of this.views) o[cn] = v.debug();
    return o;
  }

  update(world: ClientWorld, camera: THREE.Camera, dtMs: number): number {
    const dt = Math.min(0.1, dtMs / 1000);
    this.pv.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    this.frustum.setFromProjectionMatrix(this.pv);
    this.flashes.length = 0;
    // events first (they reference the pre-update state)
    for (const fe of world.frameEvents) this.onFrameEvent(world, fe);
    const seen = new Set<number>();
    let casters = 0;
    this.visibleCount = 0;
    for (const p of world.players) {
      seen.add(p.clientNum);
      const v = this.ensure(p, world);
      const local = p.clientNum === world.localClientNum;
      // local player's body is not drawn in first person (ESTIMATE: BO2 draws no full-body shadow either)
      const drawn = v.update(p, dt, this.probes, local);
      if (drawn) {
        for (const s of v.shadowProxies) s.visible = true;
        casters++;
        v.root.getWorldPosition(this.sphere.center);
        this.sphere.center.y += 36;
        this.sphere.radius = 64;
        v.root.visible = this.frustum.intersectsSphere(this.sphere);
        if (v.root.visible) this.visibleCount++;
      } else for (const s of v.shadowProxies) s.visible = false;
    }
    for (const [cn, v] of this.views) if (!seen.has(cn)) this.remove(cn, v);
    return casters;
  }

  private onFrameEvent(world: ClientWorld, fe: FrameEvent): void {
    if (fe.src === 'ent') {
      const p = world.getPlayer(fe.ent);
      const v = this.views.get(fe.ent);
      if (!p || !v) return;
      v.onEvent(fe.ev, fe.parm, p);
      if ((fe.ev === PEv.WEAPON_FIRE || fe.ev === PEv.WEAPON_FIRE_LAST) && v.visible) this.flashes.push(v.muzzle(new THREE.Vector3()));
    } else if (fe.src === 'game' && fe.ge.type === GameEventType.DEATH) {
      const ge = fe.ge;
      const p = world.getPlayer(ge.ent);
      const v = this.views.get(ge.ent);
      if (p && v) v.onDeath(p, ge.mod, ge.dirYaw);
    }
  }

  private ensure(p: RenderPlayer, world: ClientWorld): PlayerView {
    let v = this.views.get(p.clientNum);
    const body = (world.playerInfo.get(p.clientNum)?.body ?? p.info?.body ?? 0) & 1;
    if (v && v.bodyIndex !== body) { this.remove(p.clientNum, v); v = undefined; }
    if (!v) {
      v = new PlayerView(p.clientNum, body, this.assets, this.U, this.shadowMat);
      this.scene.add(v.root);
      for (const s of v.shadowProxies) this.shadowScene.add(s);
      this.views.set(p.clientNum, v);
    }
    return v;
  }

  private remove(cn: number, v: PlayerView): void {
    this.scene.remove(v.root);
    for (const s of v.shadowProxies) this.shadowScene.remove(s);
    v.dispose();
    this.views.delete(cn);
  }

  /**
   * Load-time warm-up: one hidden view per body with every weapon, so the skinned-model programs exist and
   * their textures are on the GPU before the first match frame (verify perf-spikes: 0.5 s frames at
   * MATCH/SPAWN). The views stay in the scene, hidden, so three keeps their programs (a program is deleted
   * when its last material is disposed). `show` toggles them for the warm-up render.
   */
  prewarm(at: THREE.Vector3): (show: boolean) => void {
    const warm: PlayerView[] = [];
    for (let b = 0; b < this.assets.bodies.length && b < 2; b++) {
      const v = new PlayerView(-1 - b, b, this.assets, this.U, this.shadowMat);
      v.root.position.copy(at).add(new THREE.Vector3(b * 40, 0, 0));
      v.root.name = `warm_player_${b}`;
      v.showAllWeapons(true);
      this.scene.add(v.root);
      for (const s of v.shadowProxies) this.shadowScene.add(s);
      warm.push(v);
    }
    this.warm = warm;
    return (show) => {
      for (const v of warm) {
        v.root.visible = show;
        for (const s of v.shadowProxies) s.visible = show;
      }
    };
  }
  private warm: PlayerView[] = [];

  dispose(): void {
    for (const [cn, v] of this.views) this.remove(cn, v);
    for (const v of this.warm) { this.scene.remove(v.root); for (const s of v.shadowProxies) this.shadowScene.remove(s); v.dispose(); }
    this.warm = [];
  }
}

export type { PartAction };
